import { z } from "zod";
import { StepSpecSchema, StepEdgeSchema } from "./stepSpec.js";
/**
 * PlaybookDiff describes the structural difference between the current
 * StepGraph of a Play and a freshly proposed StepGraph (e.g. from a
 * re-decompose with new params or a Discovery re-run).
 *
 * Semantics:
 *  - additions: full StepSpec objects to insert.
 *  - removals: step ids in the current Play that should be marked as
 *    superseded. The diff algorithm MUST exclude protected steps
 *    (completed / running / awaiting_review) from this list — those
 *    are already executing or have produced effects we will not erase.
 *  - protectedRemovals: candidate ids that WOULD have been removed but
 *    were filtered because their state is protected. Useful for
 *    surfacing "we kept these because they were already in flight".
 *  - modifications: per-step partial field changes (title, inputs, ...)
 *    safe to apply in place. Protected steps are never modified.
 *  - edgeAdditions / edgeRemovals: symmetric difference on the edge set.
 */
export const PlaybookDiffSchema = z.object({
    additions: z.array(StepSpecSchema),
    removals: z.array(z.string()),
    protectedRemovals: z.array(z.string()),
    modifications: z.array(z.object({
        stepId: z.string(),
        changes: z.object({
            title: z.string().optional(),
            agentCapability: z.string().optional(),
            inputs: z.record(z.string(), z.unknown()).optional(),
            requiresApproval: z.boolean().optional(),
        }),
    })),
    edgeAdditions: z.array(StepEdgeSchema),
    edgeRemovals: z.array(z.tuple([z.string(), z.string()])),
});
